import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MuiPaper from '@mui/material/Paper';
import Edit from '@mui/icons-material/Edit';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { apiUrl, fetchJson } from '../../apiBase';

const api = {
  planGet: (school, studentId) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/plan/${encodeURIComponent(studentId)}`),
  planSave: (school) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/plan`),
  installments: (school, studentId) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/student/${encodeURIComponent(studentId)}`),
  create: (school) => apiUrl(`/admin/payments/${encodeURIComponent(school)}`),
  update: (school, studentId, no) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/student/${encodeURIComponent(studentId)}/${no}`),
  del: (school, studentId, no) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/student/${encodeURIComponent(studentId)}/${no}`),
  markPaid: (school, studentId, no) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/student/${encodeURIComponent(studentId)}/${no}/mark-paid`),
  summary: (school, cls) => apiUrl(`/admin/payments/${encodeURIComponent(school)}/summary` + (cls ? `?class_name=${encodeURIComponent(cls)}` : '')),
  classes: (school) => apiUrl(`/admin/classes/${encodeURIComponent(school)}`),
  studentsAll: (school) => apiUrl(`/admin/students/${encodeURIComponent(school)}/all`),
};

export default function PaymentsManager({ schoolId, onError, onSuccess }) {
  // Visibility toggle for sensitive monetary values
  const [showAmounts, setShowAmounts] = useState(false);
  const [classFilter, setClassFilter] = useState('');
  const [classes, setClasses] = useState([]);
  const [classQuery, setClassQuery] = useState(''); // search input value
  const [classListOpen, setClassListOpen] = useState(false);
  const [classAnchorEl, setClassAnchorEl] = useState(null);
  const [summary, setSummary] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [planDialog, setPlanDialog] = useState(false);
  const [planForm, setPlanForm] = useState({ total_amount: '', advance_payment: '0', installments_count: '', final_due_date: '2026-06-15', regenerate: true });
  const [planData, setPlanData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
  const headers = useMemo(() => (
    token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
  ), [token]);

  const loadMeta = useCallback(async () => {
    try {
  const cls = await fetchJson(api.classes(schoolId), { headers }).then(r => r.json()).catch(()=>({classes:[]}));
      const classList = Array.isArray(cls) ? cls : (cls.classes || []);
  const names = classList.map(c => (c?.name ? c.name : c)).filter(Boolean);
      setClasses(names);
    } catch(e) { /* ignore */ }
  }, [schoolId, headers]);

  const loadSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
  const data = await fetchJson(api.summary(schoolId, classFilter), { headers }).then(r => r.json());
      setSummary(Array.isArray(data) ? data : []);
    } catch(e) { onError && onError('Özet yüklenemedi'); }
    finally { setLoadingSummary(false); }
  }, [schoolId, classFilter, headers, onError]);

  const loadInstallments = useCallback(async (studentId) => {
    if (!studentId) return;
    try {
      setLoadingInstallments(true);
  const data = await fetchJson(api.installments(schoolId, studentId), { headers }).then(r => r.json());
      setInstallments(Array.isArray(data) ? data : []);
    } catch(e) { setInstallments([]); }
    finally { setLoadingInstallments(false); }
  }, [schoolId, headers]);

  useEffect(()=> { loadMeta(); }, [loadMeta]);
  useEffect(()=> { loadSummary(); }, [loadSummary]);

  const loadPlan = useCallback(async (studentId) => {
    try {
  const data = await fetchJson(api.planGet(schoolId, studentId), { headers }).then(r=> r.json());
      if (data && data.total_amount !== undefined) {
        setPlanData(data);
        setPlanForm(p => ({ ...p,
          total_amount: String(data.total_amount || ''),
          advance_payment: String(data.advance_payment || '0'),
          installments_count: String(data.installments_count || ''),
          final_due_date: data.final_due_date || '2026-06-15',
        }));
      }
    } catch(e) { /* ignore */ }
  }, [schoolId, headers]);

  useEffect(() => {
    if (selectedStudent) {
      const sid = selectedStudent.student_id || selectedStudent.id;
      loadInstallments(sid);
      loadPlan(sid);
    }
  }, [selectedStudent, loadInstallments, loadPlan]);

  // Manual create fully removed per requirement
  const openPlan = () => { if (!selectedStudent) { onError && onError('Öğrenci seçin'); return; } setPlanDialog(true); };
  // Manual save removed

  const markPaid = async (inst) => {
    try {
  const res = await fetchJson(api.markPaid(schoolId, inst.student_id, inst.installment_no), { method: 'POST', headers, body: JSON.stringify({}) });
      if (!res.ok) throw new Error();
      onSuccess && onSuccess('Ödeme işaretlendi');
      loadInstallments(inst.student_id);
      loadSummary();
    } catch(e) { onError && onError('İşlem başarısız'); }
  };

  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ due_date:'', amount:'', paid:false, installment_no:null });

  const openEdit = (inst) => {
    setEditForm({ due_date: inst.due_date || '', amount: String(inst.amount||''), paid: !!inst.paid, installment_no: inst.installment_no });
    setEditDialog(true);
  };

  const saveEdit = async () => {
    try {
      const instNo = editForm.installment_no;
      const body = {
        due_date: editForm.due_date,
        amount: editForm.amount ? parseFloat(editForm.amount) : undefined,
        paid: editForm.paid,
        recalc_remaining: true,
      };
  const res = await fetchJson(api.update(schoolId, selectedStudent.student_id || selectedStudent.id, instNo), { method:'PUT', headers, body: JSON.stringify(body) });
      if(!res.ok) throw new Error();
      onSuccess && onSuccess('Güncellendi');
      setEditDialog(false);
      loadInstallments(selectedStudent.student_id || selectedStudent.id);
      loadSummary();
    } catch(e) { onError && onError('Güncellenemedi'); }
  };

  const savePlan = async () => {
    try {
      if (!selectedStudent) { onError && onError('Öğrenci seçin'); return; }
      const body = {
        student_id: selectedStudent.student_id || selectedStudent.id,
        total_amount: parseFloat(planForm.total_amount || '0'),
        advance_payment: parseFloat(planForm.advance_payment || '0'),
        installments_count: planForm.installments_count ? parseInt(planForm.installments_count,10) : null,
        final_due_date: planForm.final_due_date || null,
        regenerate: planForm.regenerate,
      };
  const res = await fetchJson(api.planSave(schoolId), { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      onSuccess && onSuccess('Ödeme planı kaydedildi');
      setPlanDialog(false);
      loadInstallments(body.student_id);
      loadSummary();
    } catch(e) { onError && onError('Plan kaydedilemedi'); }
  };

  const formatCurrency = (v) => (v || v === 0) ? Intl.NumberFormat('tr-TR', { style:'currency', currency:'TRY', maximumFractionDigits:2 }).format(v) : '';
  const maybeMask = (v, formatter = formatCurrency) => showAmounts ? formatter(v) : (v || v === 0 ? '***' : '***');
  const formatDate = (iso) => {
    if(!iso) return '-';
    try { return new Date(iso).toLocaleDateString('tr-TR', { day:'2-digit', month:'short', year:'numeric' }); } catch { return iso; }
  };

  const emptySummary = summary.length === 0;
  const sortedClasses = [...classes].sort((a,b)=> String(a).localeCompare(String(b), 'tr-TR'));
  const filteredClasses = sortedClasses.filter(n => !classQuery || String(n).toLocaleLowerCase('tr-TR').includes(classQuery.toLocaleLowerCase('tr-TR')));

  const openClassPopover = (e) => {
    setClassAnchorEl(e.currentTarget);
    setClassListOpen(true);
  };
  const closeClassPopover = () => { setClassListOpen(false); };
  const handleSelectClass = (cls) => {
    setClassFilter(cls === '__ALL__' ? '' : cls);
    if (cls !== '__ALL__') setClassQuery('');
    closeClassPopover();
  };
  const emptyInstallments = installments.length === 0;
  const paidInstallmentsAmount = installments.filter(i=>i.paid).reduce((s,i)=> s + (i.amount||0),0);
  const totalPaidWithAdvance = (planData ? (planData.advance_payment || 0) : 0) + paidInstallmentsAmount;
  const remainingFromTotal = planData ? Math.max(0, (planData.total_amount || 0) - totalPaidWithAdvance) : 0;

  return (
    <Box sx={{ p:0 }}>
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3, flexWrap:'wrap' }}>
        <Box sx={{ width:48, height:48, borderRadius:'50%', background:'#E3F2FD', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <BarChartOutlinedIcon color="primary" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight:600, lineHeight:1 }}>Ödemeler</Typography>
          <Typography variant="body2" color="text.secondary">Taksit & ödeme yönetimi</Typography>
        </Box>
        <Box sx={{ flex:1 }} />
        <Button size="small" variant="outlined" onClick={()=> setShowAmounts(s=> !s)}>
          {showAmounts ? 'Tutarları Gizle' : 'Tutarları Göster'}
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p:3, borderRadius:3, display:'flex', flexDirection:'column', gap:3 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight:600 }}>Öğrenci Özetleri</Typography>
                <Typography variant="body2" color="text.secondary">Şube filtreleyip öğrenci seçin</Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={loadSummary}>Yenile</Button>
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <TextField
                label={classFilter ? `Şube: ${classFilter}` : 'Şube Seç / Ara'}
                placeholder="Şube ara..."
                size="small"
                value={classQuery}
                onFocus={openClassPopover}
                onClick={openClassPopover}
                onChange={(e)=> { setClassQuery(e.target.value); if(!classListOpen) openClassPopover(e); }}
                fullWidth
                InputProps={{
                  endAdornment: classFilter ? (
                    <Button size="small" onClick={()=> { setClassFilter(''); setClassQuery(''); }} sx={{ mr:-1 }}>Temizle</Button>
                  ) : null
                }}
              />
              <Popper open={classListOpen} anchorEl={classAnchorEl} placement="bottom-start" style={{ zIndex:1300, width: classAnchorEl?.offsetWidth || 300 }} modifiers={[{name:'offset', options:{offset:[0,4]}}]}>
                <ClickAwayListener onClickAway={closeClassPopover}>
                  <MuiPaper elevation={3} sx={{ maxHeight:260, overflowY:'auto', borderRadius:1 }}>
                    <List dense sx={{ py:0 }}>
                      <ListItemButton selected={!classFilter} onClick={()=> handleSelectClass('__ALL__')} sx={{ '&.Mui-selected': { background:'#E3F2FD' } }}>
                        <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: !classFilter ? 600:500 }}>Tümü</Typography>} />
                      </ListItemButton>
                      {filteredClasses.map(cls => {
                        const active = classFilter === cls;
                        return (
                          <ListItemButton
                            key={cls}
                            selected={active}
                            onClick={()=> handleSelectClass(cls)}
                            sx={{ '&.Mui-selected': { background:'#E3F2FD' }, py:0.5 }}
                          >
                            <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: active? 600:500 }}>{cls}</Typography>} />
                          </ListItemButton>
                        );
                      })}

                      {filteredClasses.length === 0 && (
                        <Box sx={{ px:2, py:1, color:'text.secondary', fontSize:12 }}>Şube bulunmadı</Box>
                      )}
                    </List>
                  </MuiPaper>
                </ClickAwayListener>
              </Popper>
            </Box>
            <Divider />
            {loadingSummary ? (
              <Box sx={{ py:2, display:'flex', flexDirection:'column', gap:1 }}>
                {Array.from({ length:5 }).map((_,i)=>(<Skeleton key={i} variant="rounded" height={38} />))}
              </Box>
            ) : emptySummary ? (
              <Box sx={{ py:4, textAlign:'center', color:'text.secondary' }}>Henüz ödeme planı olan öğrenci yok</Box>
            ) : (
              <Table size="small" sx={{ '& tbody tr:hover': { background:'#F9FAFB' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Öğrenci</TableCell>
                    <TableCell align="right">Toplam</TableCell>
                    <TableCell align="right">Ödenen</TableCell>
                    <TableCell align="right">Kalan</TableCell>
                    <TableCell>Sonraki</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map(row => {
                    const selected = selectedStudent?.student_id === row.student_id;
                    return (
                      <TableRow key={row.student_id} hover selected={selected} onClick={()=> setSelectedStudent(row)} sx={{ cursor:'pointer', '&.Mui-selected': { background:'#E3F2FD' } }}>
                        <TableCell>
                          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box sx={{ fontWeight:500 }}>{row.name} {row.surname}</Box>
                            <Chip size="small" label={row.class_name} />
                          </Box>
                        </TableCell>
                        <TableCell align="right">{maybeMask(row.total_amount)}</TableCell>
                        <TableCell align="right">{maybeMask(row.paid_amount)}</TableCell>
                        <TableCell align="right">{maybeMask(row.outstanding)}</TableCell>
                        <TableCell>{formatDate(row.next_due_date)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p:3, borderRadius:3, display:'flex', flexDirection:'column', gap:3 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', rowGap:2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight:600 }}>Taksitler</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedStudent ? `${selectedStudent.name} ${selectedStudent.surname} – ${selectedStudent.class_name}` : 'Öğrenci seçilmedi'}
                </Typography>
              </Box>
              <Box sx={{ display:'flex', gap:1 }}>
                <Tooltip title="Ödeme planını görüntüle / oluştur"><span><Button size="small" variant="outlined" disabled={!selectedStudent} onClick={openPlan}>Plan</Button></span></Tooltip>
              </Box>
            </Box>
            <Divider />
            {!selectedStudent ? (
              <Box sx={{ py:6, textAlign:'center', color:'text.secondary' }}>Öğrenci seçin</Box>
            ) : emptyInstallments ? (
              <Box sx={{ py:6, textAlign:'center', color:'text.secondary' }}>Henüz taksit oluşturulmamış. <Button size="small" onClick={openPlan}>Plan Oluştur</Button></Box>
            ) : (
              <>
                {planData && (
                  <Box sx={{ background:'#F9FAFB', border:'1px solid #E0E0E0', borderRadius:2, p:2, display:'flex', flexWrap:'wrap', gap:2, fontSize:13 }}>
                    <Box><strong>Toplam:</strong> {maybeMask(planData.total_amount)}</Box>
                    <Box><strong>Peşinat:</strong> {maybeMask(planData.advance_payment)}</Box>
                    <Box><strong>Ödenen:</strong> {maybeMask(totalPaidWithAdvance)}</Box>
                    <Box><strong>Kalan:</strong> {maybeMask(remainingFromTotal)}</Box>
                    <Box><strong>Son Vade:</strong> {formatDate(planData.final_due_date)}</Box>
                  </Box>
                )}
                <Box sx={{ maxHeight:420, overflowY:'auto', mt:2 }}>
                  {loadingInstallments ? (
                    <Box sx={{ py:2, display:'flex', flexDirection:'column', gap:1 }}>
                      {Array.from({ length:4 }).map((_,i)=>(<Skeleton key={i} variant="rounded" height={40} />))}
                    </Box>
                  ) : (
                    <Table size="small" sx={{ '& tbody tr:hover': { background:'#F9FAFB' }, '& td': { py:1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>No</TableCell>
                          <TableCell>Vade</TableCell>
                          <TableCell align="right">Tutar</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell width={120}>İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {installments.map(inst => (
                          <TableRow key={inst.installment_no} selected={!!inst.paid}>
                            <TableCell>{inst.installment_no}</TableCell>
                            <TableCell>{formatDate(inst.due_date)}</TableCell>
                            <TableCell align="right">{maybeMask(inst.amount)}</TableCell>
                            <TableCell>{inst.paid ? <Chip size="small" label="Ödendi" sx={{ bgcolor:'#E8F5E9', color:'#2E7D32', fontWeight:500 }} /> : <Chip size="small" label="Bekliyor" sx={{ bgcolor:'#FFF3E0', color:'#ED6C02', fontWeight:500 }} />}</TableCell>
                            <TableCell>
                              <Box sx={{ display:'flex', gap:1 }}>
                                {!inst.paid && <Button size="small" onClick={()=> markPaid(inst)} aria-label={`taksit ${inst.installment_no} ödendi işaretle`}>Ödendi</Button>}
                                <Tooltip title="Düzenle"><span><IconButton size="small" onClick={()=> openEdit(inst)} aria-label={`taksit ${inst.installment_no} düzenle`} sx={{ '&:hover': { background:'#F5F5F5' } }}><Edit fontSize="small" /></IconButton></span></Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

  {/* Manual taksit ekleme tamamen kaldırıldı */}

      <Dialog open={planDialog} onClose={()=> setPlanDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ödeme Planı</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="Toplam Tutar (₺)" size="small" fullWidth value={planForm.total_amount} onChange={e=> setPlanForm(p=>({...p,total_amount:e.target.value}))} type="number" /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Peşinat (₺)" size="small" fullWidth value={planForm.advance_payment} onChange={e=> setPlanForm(p=>({...p,advance_payment:e.target.value}))} type="number" /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Taksit Sayısı" size="small" fullWidth value={planForm.installments_count} onChange={e=> setPlanForm(p=>({...p,installments_count:e.target.value}))} helperText="Boş bırakılırsa otomatik hesaplanır" type="number" /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Son Vade" size="small" fullWidth value={planForm.final_due_date} onChange={e=> setPlanForm(p=>({...p,final_due_date:e.target.value}))} type="date" InputLabelProps={{ shrink:true }} /></Grid>
          </Grid>
          <Box sx={{ mt:2, typography:'caption', color:'text.secondary' }}>Kaydettiğinizde mevcut bekleyen taksitler yeniden oluşturulur ve ödeme durumları sıfırlanır.</Box>
        </DialogContent>
        <DialogActions sx={{ px:3, py:2 }}>
          <Button onClick={()=> setPlanDialog(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={savePlan}>Kaydet</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialog} onClose={()=> setEditDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Taksit Düzenle #{editForm.installment_no}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
            <TextField label="Vade Tarihi" size="small" value={editForm.due_date} onChange={e=> setEditForm(f=>({...f,due_date:e.target.value}))} type="date" InputLabelProps={{ shrink:true }} />
            <TextField label="Tutar (₺)" size="small" value={editForm.amount} onChange={e=> setEditForm(f=>({...f,amount:e.target.value}))} type="number" helperText="Tutar değişirse sonraki bekleyen taksitler yeniden dağılır" />
            <Box sx={{ display:'flex', gap:1 }}>
              <Button size="small" variant={editForm.paid? 'contained':'outlined'} color={editForm.paid? 'success':'primary'} onClick={()=> setEditForm(f=>({...f, paid: !f.paid}))}>{editForm.paid? 'Ödendi':'Bekliyor'}</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px:3, py:2 }}>
          <Button onClick={()=> setEditDialog(false)}>Kapat</Button>
          <Button variant="contained" onClick={saveEdit}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
